import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '@/hooks/useLang';
import { uiContent } from '@/data/ui';
import { categories, MAIN_SITE_URL } from '@/data/categories';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const lang = useLang();
  const t = uiContent[lang];
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 80);
    }
  };

  const links: { label: string; action: () => void }[] = [
    { label: t.nav.home, action: () => { setMobileOpen(false); navigate('/'); } },
    { label: t.nav.categories, action: () => scrollTo('categories') },
    { label: t.nav.guides, action: () => scrollTo('guides') },
    { label: t.nav.reviews, action: () => navigate('/article/allchinabuy-review') },
    { label: t.nav.shipping, action: () => navigate('/article/allchinabuy-shipping') },
    { label: t.nav.warehouse, action: () => navigate('/article/allchinabuy-warehouse') },
    { label: t.nav.qc, action: () => navigate('/article/allchinabuy-qc') },
    { label: t.nav.faq, action: () => scrollTo('faq') },
    { label: t.nav.shoppingGuide, action: () => navigate('/article/allchinabuy-buying-guide') },
  ];

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-3">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-table-line text-lg"></i>
            </span>
            <span className="text-base font-bold text-foreground-950 font-heading">AllChinaBuy Spreadsheet</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.label}
                type="button"
                onClick={l.action}
                className="whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-foreground-800 transition hover:bg-background-100 hover:text-foreground-950"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex whitespace-nowrap items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              {t.nav.cta}
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-background-200 text-foreground-800 lg:hidden"
              aria-label="Menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-x-0 top-[61px] bottom-0 z-50 overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <nav className="flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.label}
                type="button"
                onClick={l.action}
                className="whitespace-nowrap rounded-lg px-3 py-2.5 text-left text-sm font-medium text-foreground-800 transition hover:bg-background-100"
              >
                {l.label}
              </button>
            ))}

            <p className="mt-4 px-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">
              {t.categoryLabel}
            </p>
            <div className="mt-1 grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 transition hover:bg-background-100"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  <span className="truncate">{cat.name[lang]}</span>
                </a>
              ))}
            </div>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-4 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
            >
              {t.nav.cta}
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
          </nav>
        </div>
      )}
    </>
  );
}